﻿import { COVENANTS_DATE_TIME_FORMAT } from '~/modules/covenants/constants.tsx';
import {
  MANAGER_APPLICATION_REVIEW_ID_PREFIX,
  MANAGER_APPLICATION_REVIEW_SUBTITLE_SEPARATOR,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import { formatDate } from '~/modules/covenants/utils.ts';
import {
  StatusFilterValue,
  type TApiSupervisorApplication,
  type TApiSupervisorApplicationDocument,
  type TCovenantsDocumentStableKeySource,
} from '~/modules/covenants/types.ts';
import type {
  TManagerApplicationDecision,
  TManagerReviewFile,
  TManagerReviewTableRow,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import { getCommentPlainText } from '~/components/CommentCell/utils.ts';

export const buildManagerDocumentKey = ({
  documentId,
  yearPeriod,
  childPeriod,
  deadline,
}: TCovenantsDocumentStableKeySource) =>
  `${String(documentId)}_${yearPeriod || ''}_${childPeriod || ''}_${deadline || ''}`;

const buildManagerReviewFileKey = (
  document: TApiSupervisorApplicationDocument,
  fileId?: string,
  fileName?: string,
  fileIndex?: number
) =>
  [
    buildManagerDocumentKey(document),
    fileId ?? 'no-id',
    fileName ?? 'no-file-name',
    fileIndex !== undefined ? String(fileIndex) : undefined,
  ]
    .filter(Boolean)
    .join('__');

export const normalizeManagerDocumentFiles = (
  document: TApiSupervisorApplicationDocument
): TManagerReviewFile[] => {
  const files = Array.isArray(document.files) ? document.files : [];

  if (files.length) {
    return files
      .filter((file) => !!file.id)
      .map((file, index) => ({
        id: file.id,
        key: buildManagerReviewFileKey(document, file.id, file.fileName, index),
        documentName: document.documentName,
        documentId: String(document.documentId),
        fileName: file.fileName,
        comment: file.comment,
        createdAt: file.createdAt,
        status: file.status ?? StatusFilterValue.New,
        hasSignedFile: file.hasSignedFile,
        signedByName: file.signedByName,
        signetByCode: file.signetByCode,
        yearPeriod: document.yearPeriod,
        childPeriod: document.childPeriod,
        version: document.version,
      }));
  }

  if (!document.id) {
    return [];
  }

  return [
    {
      id: document.id,
      key: buildManagerReviewFileKey(document, document.id, document.fileName),
      documentName: document.documentName,
      documentId: String(document.documentId),
      fileName: document.fileName,
      comment: document.comment,
      createdAt: document.createdAt,
      status: document.status ?? StatusFilterValue.New,
      hasSignedFile: document.hasSignedFile,
      signedByName: document.signedByName,
      signetByCode: document.signetByCode,
      yearPeriod: document.yearPeriod,
      childPeriod: document.childPeriod,
      version: document.version,
    },
  ];
};

export const buildManagerReviewTableRows = (
  documents: TApiSupervisorApplicationDocument[]
): TManagerReviewTableRow[] =>
  documents.flatMap((document) => {
    const documentKey = buildManagerDocumentKey(document);
    const files = normalizeManagerDocumentFiles(document);

    return [
      {
        type: 'document' as const,
        key: documentKey,
        document,
        hasFiles: files.length > 0,
      },
      ...files.map((file) => ({
        type: 'file' as const,
        key: file.key,
        documentKey,
        file,
      })),
    ];
  });

export const formatManagerDocumentDeadline = (value?: string) => (value ? formatDate(value) : '-');

export const formatManagerDocumentPeriod = ({
  childPeriod,
  yearPeriod,
}: Pick<TApiSupervisorApplicationDocument, 'childPeriod' | 'yearPeriod'>) =>
  [childPeriod, yearPeriod].filter(Boolean).join(' / ') || '-';

export const formatManagerSignerLabel = ({
  signedByName,
  signetByCode,
}: Pick<TManagerReviewFile, 'signedByName' | 'signetByCode'>) =>
  `${signedByName || '-'}${signetByCode ? ` (${signetByCode})` : ''}`;

export const getManagerDocumentCommentValue = (
  documentId: string,
  comments: Record<string, string>,
  documentsById: Record<string, TManagerReviewFile>
) => comments[documentId] ?? documentsById[documentId]?.comment ?? '';

export const getManagerApplicationPageSubtitle = (
  application?: TApiSupervisorApplication | null,
  applicationId?: string
) => {
  if (!application) {
    return `${MANAGER_APPLICATION_REVIEW_ID_PREFIX}${applicationId ?? '-'}`;
  }

  const parts = [application.contragentFullName, application.contragentCode].filter(Boolean);

  return (
    parts.join(MANAGER_APPLICATION_REVIEW_SUBTITLE_SEPARATOR) ||
    `${MANAGER_APPLICATION_REVIEW_ID_PREFIX}${application.id}`
  );
};

export const getManagerApplicationCreatedAtLabel = (
  application?: Pick<TApiSupervisorApplication, 'createdAt'> | null
) =>
  application?.createdAt
    ? `Створено ${formatDate(application.createdAt, COVENANTS_DATE_TIME_FORMAT)}`
    : null;

export const mergeManagerDocumentsById = (
  previousState: Record<string, TManagerReviewFile>,
  documents: TApiSupervisorApplicationDocument[]
) => {
  const nextState = { ...previousState };

  documents.flatMap(normalizeManagerDocumentFiles).forEach((documentFile) => {
    nextState[documentFile.id] = documentFile;
  });

  return nextState;
};

export const mergeManagerDocumentsByKey = (
  previousState: Record<string, TApiSupervisorApplicationDocument>,
  documents: TApiSupervisorApplicationDocument[]
) => {
  const nextState = { ...previousState };

  documents.forEach((document) => {
    nextState[buildManagerDocumentKey(document)] = document;
  });

  return nextState;
};

export const buildManagerApplicationReviewPayload = (
  decisions: Record<string, TManagerApplicationDecision>,
  comments: Record<string, string>,
  documentsById: Record<string, TManagerReviewFile>
) => ({
  accept: {
    documents: Object.entries(decisions)
      .filter(([, decision]) => decision === 'accept')
      .map(([documentId]) => ({ id: documentId })),
  },
  deny: {
    documents: Object.entries(decisions)
      .filter(([, decision]) => decision === 'reject')
      .map(([documentId]) => ({
        id: documentId,
        comment: getManagerDocumentCommentValue(documentId, comments, documentsById),
      })),
  },
});

export const areAllManagerDocumentDecisionsSelected = (
  decisions: Record<string, TManagerApplicationDecision>,
  documentsById: Record<string, TManagerReviewFile>,
  loadedDocumentsByKey: Record<string, TApiSupervisorApplicationDocument>,
  totalElements: number
) => {
  const loadedDocumentsCount = Object.keys(loadedDocumentsByKey).length;
  const allDocumentsLoaded = loadedDocumentsCount === totalElements;

  if (!allDocumentsLoaded || totalElements === 0) {
    return false;
  }

  if (!Object.keys(documentsById).length) {
    return false;
  }

  return Object.values(documentsById).every((document) => {
    const decision = decisions[document.id];

    return decision === 'accept' || decision === 'reject';
  });
};

export const areAllRejectedManagerDocumentsCommented = (
  decisions: Record<string, TManagerApplicationDecision>,
  comments: Record<string, string>,
  documentsById: Record<string, TManagerReviewFile>
) =>
  Object.values(documentsById).every((document) => {
    if (decisions[document.id] !== 'reject') {
      return true;
    }

    return !!getCommentPlainText(
      getManagerDocumentCommentValue(document.id, comments, documentsById)
    ).trim().length;
  });

export const getRejectedManagerDocumentsWithoutComments = (
  decisions: Record<string, TManagerApplicationDecision>,
  comments: Record<string, string>,
  documentsById: Record<string, TManagerReviewFile>
) =>
  Object.values(documentsById).filter((document) => {
    if (decisions[document.id] !== 'reject') {
      return false;
    }

    return !getCommentPlainText(
      getManagerDocumentCommentValue(document.id, comments, documentsById)
    ).trim().length;
  });

export const applyCommentToRejectedManagerDocuments = (
  decisions: Record<string, TManagerApplicationDecision>,
  comments: Record<string, string>,
  documentsById: Record<string, TManagerReviewFile>,
  sharedComment: string
) => {
  const normalizedComment = sharedComment.trim();

  if (!normalizedComment) {
    return comments;
  }

  return getRejectedManagerDocumentsWithoutComments(decisions, comments, documentsById).reduce<
    Record<string, string>
  >(
    (nextComments, document) => ({
      ...nextComments,
      [document.id]: normalizedComment,
    }),
    { ...comments }
  );
};
